import { FieldValues } from 'react-hook-form';
import { PasswordInputProps } from './props';
import styles from './styles.module.scss';
import { Input } from '../Input';
import zxcvbn, { ZXCVBNResult } from 'zxcvbn';
import { ChangeEvent, useState } from 'react';
import { BarPasswordStrong } from './BarPasswordStrong';
import { useTranslation } from '../../hooks';

/**
 * PasswordInput Component
 * A form input component for password fields with strength validation and visual feedback.
 * Uses zxcvbn for password strength analysis and displays a strength indicator bar.
 *
 * @component
 * @template T - Type extending FieldValues from react-hook-form
 * @param {Object} props - Component props
 * @param {string} props.name - Field name for form registration
 * @param {boolean} props.isDirty - Whether the field has been modified
 * @param {Function} props.register - react-hook-form register function
 * @param {string} [props.password=''] - Current password value
 * @param {boolean} props.isTouched - Whether the field has been touched/focused
 * @returns {JSX.Element} Password input with strength indicator
 *
 * @example
 * // Using with react-hook-form
 * <PasswordInput<FormData>
 *   name="password"
 *   isDirty={formState.isDirty}
 *   register={register}
 *   password={watch('password')}
 *   isTouched={formState.touchedFields.password}
 * />
 */
export function PasswordInput<T extends FieldValues>({
  name,
  isDirty,
  register,
  password = '',
  isTouched,
}: PasswordInputProps<T>) {
  const { t } = useTranslation('register');
  const { t: tCommon } = useTranslation('common');
  const [passwordStrength, setPasswordStrength] = useState<ZXCVBNResult | null>(
    null,
  );
  const isPasswordEmpty: boolean = password?.length === 0;
  const isValidPasswordLength: boolean =
    password?.length >= 4 && password?.length <= 128;
  const isValidPassword: boolean = isValidPasswordLength && !isPasswordEmpty;

  /**
   * Handles password input changes
   * Updates password strength using zxcvbn analysis
   *
   * @param {ChangeEvent<HTMLInputElement>} e - Change event from input
   * @example
   * handlePasswordChange({ target: { value: 'newPassword123' } })
   */
  function handlePasswordChange(e: ChangeEvent<HTMLInputElement>) {
    const newPassword = e.target.value;
    setPasswordStrength(zxcvbn(newPassword));
  }

  /**
   * Gets appropriate error message for password validation
   * Returns translation key based on validation state
   *
   * @returns {string|undefined} Error message or undefined if valid
   * @example
   * const errorMsg = getPasswordError() // Returns 'empty_password' or 'invalid_password_length'
   */
  function getPasswordError(): string | undefined {
    if (isPasswordEmpty) {
      return tCommon('empty_password');
    }

    if (!isValidPasswordLength) {
      return tCommon('invalid_password_length');
    }
  }

  return (
    <div className={styles.container}>
      <Input
        name={name}
        type="password"
        className={isValidPassword ? styles.container__input : undefined}
        placeholder={t('password_placeholder')}
        register={register}
        registerOptions={{
          required: true,
          onChange: handlePasswordChange,
        }}
        isError={!isValidPassword && isTouched}
        errorMessage={getPasswordError()}
        icon={isValidPassword ? 'fa-solid fa-circle-check' : undefined}
        hasPaddingRight
      />

      {isDirty && !!passwordStrength && password !== '' && (
        <BarPasswordStrong
          forceScore={Number(passwordStrength?.score) as 0 | 1 | 2 | 3 | 4}
        />
      )}
    </div>
  );
}
